import type { Account } from '@api/hooks/useAccounts.ts';
import { SuccessIcon } from '@gurupay/gp-ui-library';
import clsx from 'clsx';
import type { FC, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

export interface AccountItemProps {
	account: Account;
	isSelected?: boolean;
	onSelect: (account: Account) => void;
	className?: string;
	isDisabled?: boolean;
	hideBalance?: boolean;
}

export const AccountItem: FC<AccountItemProps> = ({
	account,
	isSelected,
	onSelect,
	className,
	isDisabled,
	hideBalance,
}) => {
	const { t } = useTranslation('', { keyPrefix: 'common.accountHeaders' });
	const handleSelect = () => {
		if (isDisabled) {
			return;
		}

		onSelect(account);
	};
	const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (!isDisabled && (event.key === 'Enter' || event.key === ' ')) {
			handleSelect();
		}
	};

	return (
		<div
			aria-pressed={isSelected}
			className={clsx(
				`grid px-space-8 py-space-8 items-start gap-space-16 rounded-lg transition transition-color duration-300 ease-linear last:mb-space-16
				  max-[480px]:grid-cols-[16px_1fr]
          max-mobile:[&>*:first-child]:col-[1]
          max-mobile:[&>*:first-child]:row-[1]
					max-mobile:[&>*:not(:first-child)]:col-[2]
				`,
				hideBalance
					? 'grid-cols-[32px_auto_1fr_70px_32px]'
					: 'grid-cols-[32px_auto_1fr_70px_150px_32px]',
				isSelected ? 'bg-brand-secondary-50' : 'hover:bg-neutral-50',
				isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
				className,
			)}
			onClick={handleSelect}
			onKeyDown={handleKeyDown}
			role="button"
			tabIndex={isDisabled ? -1 : 0}
		>
			{isSelected ? (
				<SuccessIcon className="w-4.5 h-4.5 mx-space-8 my-space-4 text-brand-secondary-700" />
			) : (
				<div className="w-space-16 h-space-16 bg-white rounded-xl border mx-space-8 my-space-4" />
			)}

			<div>
				<div className="text-sm-medium text-neutral-800">{t('title')}</div>
				<div className="text-sm-bold">{account.ownerName}</div>
			</div>

			<div>
				<div className="text-sm-medium text-neutral-800">{t('number')}</div>
				<div className="text-sm-bold">{account.iban}</div>
			</div>

			<div className={clsx(hideBalance && 'min-mobile:text-right')}>
				<div className="text-sm-medium text-neutral-800">{t('currency')}</div>
				<div className="text-sm-bold">{account.currency}</div>
			</div>

			{!hideBalance && (
				<div className="min-mobile:text-right">
					<div className="text-sm-medium text-neutral-800">{t('balance')}</div>
					<div className="text-sm-bold">{account.availableBalance ?? '-'}</div>
				</div>
			)}
		</div>
	);
};
